import type { SceneTracks, TimelineElement, TimelineTrack } from "@/timeline/types";
import type { RippleAdjustment } from "./apply";

interface Interval {
	startTime: number;
	endTime: number;
}

interface ElementSpan extends Interval {
	id: string;
}

/**
 * Diffs before/after track state to find where the edit needs to make other elements follow,
 * tagged by which side should move:
 * - "after" adjustments: elements at/after a boundary shift — either PULLED BACK to close a gap
 *   an element's END opened up by getting shorter (shrink), or PUSHED FORWARD because an
 *   element's END moved later — by growing, or by a plain rightward move (both count as "later")
 *   — into space that used to hold whatever was already there. Both are the "ripple right"
 *   toggle (content after an edit follows it, whichever way the edit went).
 * - "before" adjustments: the mirror image on the START side — elements at/before a boundary get
 *   PUSHED FORWARD to close a gap opened by an element's START moving later (trimmed from the
 *   left), or PULLED BACK because an element's START moved earlier (grew backward, revealing
 *   more of the source, or a plain leftward move). Both are "ripple left".
 * All four are independent and gated by direction alone (caller — CommandManager.applyRippleIfEnabled
 * — filters on it); this function always computes all of them, unconditionally.
 *
 * Known limitation on plain MOVE (not resize): the push cases measure delta from the moved
 * element's OWN before/after position, not from where the next element on the track actually
 * sits. If there was already slack (a gap) between the moved element and its neighbor larger
 * than the move distance, the neighbor still gets pushed by the full delta — introducing a new
 * gap instead of just closing the old one. This matches how ripple trim already behaves (trim
 * deltas always propagate in full) and covers the common case (adjacent, gap-free clips)
 * correctly; true "push only as far as actually needed" would require a different, greedier
 * push-resolution model than this before/after diff.
 */
export function computeRippleAdjustments({
	beforeTracks,
	afterTracks,
}: {
	beforeTracks: SceneTracks;
	afterTracks: SceneTracks;
}): RippleAdjustment[] {
	const beforeTrackList = [
		...beforeTracks.overlay,
		beforeTracks.main,
		...beforeTracks.audio,
	];
	const afterTrackList = [
		...afterTracks.overlay,
		afterTracks.main,
		...afterTracks.audio,
	];
	const afterTracksById = new Map(afterTrackList.map((track) => [track.id, track]));
	const allAfterElementIds = new Set(
		afterTrackList.flatMap((track) => track.elements.map((element) => element.id)),
	);

	return beforeTrackList.flatMap((beforeTrack): RippleAdjustment[] =>
		computeTrackRippleAdjustments({
			trackId: beforeTrack.id,
			beforeElements: beforeTrack.elements,
			afterElements: afterTracksById.get(beforeTrack.id)?.elements ?? [],
			allAfterElementIds,
		}),
	);
}

function computeTrackRippleAdjustments({
	trackId,
	beforeElements,
	afterElements,
	allAfterElementIds,
}: {
	trackId: string;
	beforeElements: TimelineElement[];
	afterElements: TimelineElement[];
	allAfterElementIds: Set<string>;
}): RippleAdjustment[] {
	const beforeElementsById = buildElementSpanMap({ elements: beforeElements });
	const afterElementsById = buildElementSpanMap({ elements: afterElements });
	const {
		vacatedAfterIntervals,
		vacatedBeforeIntervals,
		joinedIntervals,
		growthAdjustments,
	} = collectTrackIntervals({
		trackId,
		beforeElementsById,
		afterElementsById,
		allAfterElementIds,
	});
	const freedAfterIntervals = subtractIntervalSets({
		sourceIntervals: vacatedAfterIntervals,
		overlappingIntervals: joinedIntervals,
	});
	const freedBeforeIntervals = subtractIntervalSets({
		sourceIntervals: vacatedBeforeIntervals,
		overlappingIntervals: joinedIntervals,
	});

	return [
		...buildShrinkAdjustments({ trackId, intervals: freedAfterIntervals, direction: "after" }),
		...buildShrinkAdjustments({ trackId, intervals: freedBeforeIntervals, direction: "before" }),
		...growthAdjustments,
	];
}

function buildElementSpanMap({
	elements,
}: {
	elements: TimelineElement[];
}): Map<string, ElementSpan> {
	return new Map(
		elements.map((element) => [
			element.id,
			{
				id: element.id,
				startTime: element.startTime,
				endTime: element.startTime + element.duration,
			},
		]),
	);
}

function collectTrackIntervals({
	trackId,
	beforeElementsById,
	afterElementsById,
	allAfterElementIds,
}: {
	trackId: string;
	beforeElementsById: Map<string, ElementSpan>;
	afterElementsById: Map<string, ElementSpan>;
	allAfterElementIds: Set<string>;
}): {
	vacatedAfterIntervals: Interval[];
	vacatedBeforeIntervals: Interval[];
	joinedIntervals: Interval[];
	growthAdjustments: RippleAdjustment[];
} {
	const vacatedAfterIntervals: Interval[] = [];
	const vacatedBeforeIntervals: Interval[] = [];
	const joinedIntervals: Interval[] = [];
	const growthAdjustments: RippleAdjustment[] = [];

	for (const beforeElement of beforeElementsById.values()) {
		const afterElement = afterElementsById.get(beforeElement.id);
		if (!afterElement) {
			const wasMovedToAnotherTrack = allAfterElementIds.has(beforeElement.id);
			if (!wasMovedToAnotherTrack) {
				// A fully removed/deleted element always closes from the right (classic ripple
				// delete — subsequent content follows into the gap), not split between both
				// directions — there's no partial-element left to anchor a "before" close to.
				pushInterval({
					intervals: vacatedAfterIntervals,
					startTime: beforeElement.startTime,
					endTime: beforeElement.endTime,
				});
			}
			continue;
		}

		if (beforeElement.endTime > afterElement.endTime) {
			pushInterval({
				intervals: vacatedAfterIntervals,
				startTime: afterElement.endTime,
				endTime: beforeElement.endTime,
			});
		} else if (afterElement.endTime > beforeElement.endTime) {
			// Grew (or moved) later — push whatever was already at/after the OLD end forward by
			// exactly how much this element's own end moved. See the file-level doc comment for
			// the known "pre-existing gap" limitation this carries on plain moves.
			growthAdjustments.push({
				trackId,
				direction: "after",
				boundaryTime: beforeElement.endTime,
				delta: afterElement.endTime - beforeElement.endTime,
			});
		}

		if (afterElement.startTime > beforeElement.startTime) {
			pushInterval({
				intervals: vacatedBeforeIntervals,
				startTime: beforeElement.startTime,
				endTime: afterElement.startTime,
			});
		} else if (afterElement.startTime < beforeElement.startTime) {
			// Grew (or moved) earlier — pull whatever was already at/before the OLD start
			// backward by exactly how much this element's own start moved.
			growthAdjustments.push({
				trackId,
				direction: "before",
				boundaryTime: beforeElement.startTime,
				delta: afterElement.startTime - beforeElement.startTime,
			});
		}
	}

	for (const afterElement of afterElementsById.values()) {
		if (beforeElementsById.has(afterElement.id)) {
			continue;
		}

		pushInterval({
			intervals: joinedIntervals,
			startTime: afterElement.startTime,
			endTime: afterElement.endTime,
		});
	}

	return {
		vacatedAfterIntervals: normalizeIntervals({ intervals: vacatedAfterIntervals }),
		vacatedBeforeIntervals: normalizeIntervals({ intervals: vacatedBeforeIntervals }),
		joinedIntervals: normalizeIntervals({ intervals: joinedIntervals }),
		growthAdjustments,
	};
}

function buildShrinkAdjustments({
	trackId,
	intervals,
	direction,
}: {
	trackId: string;
	intervals: Interval[];
	direction: RippleAdjustment["direction"];
}): RippleAdjustment[] {
	return intervals.flatMap((interval): RippleAdjustment[] => {
		const width = interval.endTime - interval.startTime;
		if (width <= 0) {
			return [];
		}

		return [
			{
				trackId,
				direction,
				// "after" gaps close by pulling content at-or-after the gap's END backward (negative
				// delta); "before" gaps close by pushing content at-or-before the gap's START forward
				// (positive delta) — see rippleShiftElements (shift.ts) for the matching comparison.
				boundaryTime: direction === "after" ? interval.endTime : interval.startTime,
				delta: direction === "after" ? -width : width,
			},
		];
	});
}

function subtractIntervalSets({
	sourceIntervals,
	overlappingIntervals,
}: {
	sourceIntervals: Interval[];
	overlappingIntervals: Interval[];
}): Interval[] {
	const normalizedSourceIntervals = normalizeIntervals({
		intervals: sourceIntervals,
	});
	const normalizedOverlappingIntervals = normalizeIntervals({
		intervals: overlappingIntervals,
	});

	return normalizedSourceIntervals.flatMap((sourceInterval) =>
		subtractSingleInterval({
			sourceInterval,
			overlappingIntervals: normalizedOverlappingIntervals,
		}),
	);
}

function normalizeIntervals({
	intervals,
}: {
	intervals: Interval[];
}): Interval[] {
	const validIntervals: Interval[] = [];
	for (const interval of intervals) {
		pushInterval({
			intervals: validIntervals,
			startTime: interval.startTime,
			endTime: interval.endTime,
		});
	}

	const sortedIntervals = validIntervals.sort(
		(leftInterval, rightInterval) =>
			leftInterval.startTime - rightInterval.startTime,
	);

	if (sortedIntervals.length === 0) {
		return [];
	}

	const mergedIntervals: Interval[] = [{ ...sortedIntervals[0] }];
	for (const interval of sortedIntervals.slice(1)) {
		const previousInterval = mergedIntervals[mergedIntervals.length - 1];
		if (interval.startTime <= previousInterval.endTime) {
			previousInterval.endTime = Math.max(
				previousInterval.endTime,
				interval.endTime,
			);
			continue;
		}

		mergedIntervals.push({ ...interval });
	}

	return mergedIntervals;
}

function subtractSingleInterval({
	sourceInterval,
	overlappingIntervals,
}: {
	sourceInterval: Interval;
	overlappingIntervals: Interval[];
}): Interval[] {
	let remainingIntervals: Interval[] = [{ ...sourceInterval }];

	for (const overlappingInterval of overlappingIntervals) {
		remainingIntervals = remainingIntervals.flatMap((remainingInterval) => {
		if (
			overlappingInterval.endTime <= remainingInterval.startTime ||
			overlappingInterval.startTime >= remainingInterval.endTime
		) {
				return [remainingInterval];
			}

			const nextIntervals: Interval[] = [];
			pushInterval({
				intervals: nextIntervals,
				startTime: remainingInterval.startTime,
				endTime: overlappingInterval.startTime,
			});
			pushInterval({
				intervals: nextIntervals,
				startTime: overlappingInterval.endTime,
				endTime: remainingInterval.endTime,
			});
			return nextIntervals;
		});

		if (remainingIntervals.length === 0) {
			return [];
		}
	}

	return remainingIntervals;
}

function pushInterval({
	intervals,
	startTime,
	endTime,
}: { intervals: Interval[]; startTime: number; endTime: number }): void {
	if (endTime <= startTime) {
		return;
	}

	intervals.push({ startTime, endTime });
}
